/**
 * LiveMapDashboard — Admin Live Tracking Map (ODE-P07-T02).
 *
 * Renders the live positions from the P07-T01 overview snapshot + WebSocket
 * live feed (both consumed UNCHANGED — no new endpoints, no protocol
 * changes) as map markers through the REQ-084 `MapProvider` abstraction,
 * with the full REQ-090 state set:
 *
 *   loading       snapshot fetch in flight
 *   ready         map + per-marker freshness badges + unfixed list
 *   empty         explicit "no operational data" note (nothing fabricated)
 *   stale         per-marker text badge ("Usang" + fix timestamp) when a fix
 *                 is older than the server-published staleAfterMs bound —
 *                 stale markers freeze at the last known fix, never
 *                 extrapolated; page-level stale summary
 *   disconnected  live feed dropped: markers freeze at the last known fix,
 *                 explicit "bukan kondisi terkini" banner + reconnect
 *                 action (re-fetch + fresh socket — the verified P07-T01
 *                 reconnect pattern); snapshot-refetch fallback reuses the
 *                 same reload loop
 *   misconfigured no map provider key and no fake provider — explicit state
 *                 (never a crash); the freshness list stays fully usable
 *   unauthorized  401 — session missing/expired
 *   blocked       403 — role denied (server-enforced; mirror is UX-only)
 *   error         503/network — retryable via the explicit reconnect loop
 *
 * Positions come EXCLUSIVELY from the server snapshot (`livePositions`);
 * this component never computes freshness bounds, never invents or
 * extrapolates coordinates, and never plots a session without a fix
 * (those are listed). Status is never encoded by color alone (text badges +
 * timestamps). Stable `data-testid`s for deterministic tests.
 *
 * Traces to: ODE-PRD-REQ-043, ODE-PRD-REQ-084, ODE-PRD-REQ-090.
 */

import { useMemo } from 'react';
import { useAuth } from '../../auth/AuthContext.js';
import { StateView } from '../../components/StateView.js';
import { hasPermission } from '../../rbac/catalog.js';
import type { OverviewFetchResult } from '../../api/client.js';
import type { ModuleState } from '../state.js';
import { useOverviewLiveFeed } from '../overview/useOverviewLiveFeed.js';
import type { WebSocketLike } from '../overview/live-feed.js';
import {
  deriveMapDerivation,
  formatAccuracy,
  formatAgeMs,
  formatFixTime,
  toMapMarkers,
  type MapMarkerRow,
} from './markers.js';
import type { MapProviderResolution } from './map-provider.js';
import { resolveMapProviderConfig, mapEnvFromImportMeta } from './map-provider.js';
import { useLiveMapProvider } from './use-map-provider.js';
import type { MapProvider } from './map-provider.js';
import type { GoogleMapsLoader } from './google-adapter.js';

/** Test seams (mirrors the overview dashboard's injectable seams). */
export interface LiveMapDashboardSeams {
  readonly createSocket?: (url: string) => WebSocketLike | null;
  readonly fetchSnapshot?: (token: string) => Promise<OverviewFetchResult>;
  readonly streamUrl?: (token: string) => string;
  /** Map provider resolution override (tests inject fake; prod reads env). */
  readonly mapResolution?: MapProviderResolution;
  /** Adapter factory seam for the lifecycle hook (tests inject spied fake). */
  readonly createProvider?: (resolution: MapProviderResolution) => MapProvider;
  /** Google adapter loader seam (tests only). */
  readonly googleLoader?: GoogleMapsLoader;
}

/** Resolves the map provider for this render (seam → env, fail-closed). */
function resolveResolution(seams: LiveMapDashboardSeams): MapProviderResolution {
  if (seams.mapResolution !== undefined) return seams.mapResolution;
  return resolveMapProviderConfig(mapEnvFromImportMeta());
}

export function LiveMapDashboard({
  seams = {},
}: {
  readonly seams?: LiveMapDashboardSeams;
} = {}): JSX.Element {
  const { session } = useAuth();
  const { data, feed, reload } = useOverviewLiveFeed({
    token: session?.token ?? null,
    createSocket: seams.createSocket,
    fetchSnapshot: seams.fetchSnapshot,
    streamUrl: seams.streamUrl,
  });

  const snapshot = data.kind === 'ready' ? data.snapshot : null;
  const derivation = useMemo(
    () => (snapshot === null ? null : deriveMapDerivation(snapshot)),
    [snapshot],
  );
  const resolution = useMemo(() => resolveResolution(seams), [seams]);
  const markers = useMemo(
    () => (derivation === null ? [] : toMapMarkers(derivation.rows)),
    [derivation],
  );

  // The map canvas always reflects the latest snapshot while the page is
  // shown; freeze-on-disconnect comes free: without new frames the markers
  // keep their last server-reported coordinates (never extrapolated).
  const mapEnabled = snapshot !== null && resolution.kind !== 'misconfigured';
  const { containerRef, mapError, mounted } = useLiveMapProvider(
    resolution,
    markers,
    mapEnabled,
    {
      ...(seams.createProvider !== undefined ? { createProvider: seams.createProvider } : {}),
      ...(seams.googleLoader !== undefined ? { googleLoader: seams.googleLoader } : {}),
    },
  );

  return (
    <section aria-labelledby="live-map-title" className="module">
      <h2 id="live-map-title">Live Tracking Map</h2>
      <p className="module-description">
        Peta posisi driver near-real-time dari snapshot + umpan langsung server
        (REQ-043), dengan kebaruan per penanda terhadap ambang usang yang
        diterbitkan server (REQ-090).
      </p>

      {snapshot !== null && (
        <p
          data-testid={
            feed === 'live'
              ? 'map-feed-live'
              : feed === 'connecting'
                ? 'map-feed-connecting'
                : 'map-feed-disconnected-chip'
          }
          className="overview-feed-chip"
          role="status"
        >
          {feed === 'live'
            ? 'Umpan langsung aktif.'
            : feed === 'connecting'
              ? 'Menghubungkan umpan langsung…'
              : 'Umpan langsung terputus.'}
        </p>
      )}

      {snapshot !== null && feed === 'disconnected' && (
        <div
          role="alert"
          data-testid="map-feed-disconnected"
          className="state state-blocked overview-banner"
        >
          <h3>Umpan langsung terputus</h3>
          <p>
            Koneksi live terputus — posisi di bawah adalah titik terakhir yang
            diketahui, bukan kondisi terkini. Posisi tidak digeser atau
            diperkirakan.
          </p>
          <button type="button" data-testid="map-reconnect" onClick={reload}>
            Sambungkan ulang
          </button>
        </div>
      )}

      {data.kind !== 'ready' ? (
        <StateView state={toModuleState(data)} onRetry={reload} />
      ) : (
        <LiveMapBody
          derivation={derivation}
          resolution={resolution}
          containerRef={containerRef}
          mapError={mapError}
          mapMounted={mounted}
        />
      )}
    </section>
  );
}

/** Maps the overview data state to the shared REQ-090 state renderer. */
function toModuleState(data: ReturnType<typeof useOverviewLiveFeed>['data']): ModuleState {
  switch (data.kind) {
    case 'loading':
      return { kind: 'loading' };
    case 'ready':
      // Unreachable (guarded by the caller); keeps the mapping exhaustive.
      return { kind: 'empty', payload: null };
    case 'unauthorized':
      return { kind: 'unauthorized' };
    case 'blocked':
      return { kind: 'blocked', message: data.message };
    case 'error':
      return { kind: 'error', message: data.message };
  }
}

function LiveMapBody({
  derivation,
  resolution,
  containerRef,
  mapError,
  mapMounted,
}: {
  readonly derivation: ReturnType<typeof deriveMapDerivation> | null;
  readonly resolution: MapProviderResolution;
  readonly containerRef: (node: HTMLDivElement | null) => void;
  readonly mapError: string | null;
  readonly mapMounted: boolean;
}): JSX.Element {
  const rows = derivation?.rows ?? [];
  const isEmpty =
    rows.length === 0 && (derivation?.unfixedDeliveryOrders.length ?? 0) === 0;

  return (
    <div data-testid="map-ready">
      {resolution.kind === 'misconfigured' && (
        <div
          role="status"
          data-testid="map-misconfigured"
          className="state state-unauthorized overview-banner"
        >
          <h3>Peta belum dikonfigurasi</h3>
          <p>{resolution.reason}</p>
          <p>
            Daftar kebaruan posisi tetap tersedia di bawah; peta akan aktif
            setelah konfigurasi penyedia peta diisi.
          </p>
        </div>
      )}

      {mapError !== null && (
        <div
          role="alert"
          data-testid="map-error"
          className="state state-error overview-banner"
        >
          <h3>Peta gagal dimuat</h3>
          <p>{mapError}</p>
        </div>
      )}

      {(derivation?.staleCount ?? 0) > 0 && (
        <div
          role="status"
          data-testid="map-stale-warning"
          className="state state-unauthorized overview-banner"
        >
          <h3>Sebagian posisi usang</h3>
          <p>
            {derivation?.staleCount} penanda lebih lama dari ambang kebaruan —
            titik terakhir yang diketahui, bukan posisi terkini.
          </p>
        </div>
      )}

      {isEmpty && (
        <p data-testid="map-empty" className="overview-empty">
          Belum ada data posisi untuk ditampilkan.
        </p>
      )}

      {resolution.kind !== 'misconfigured' && (
        <div
          ref={containerRef}
          data-testid="map-canvas"
          className="map-canvas"
          data-map-provider={resolution.kind}
          data-map-mounted={mapMounted ? 'true' : 'false'}
          aria-label="Peta posisi driver"
        />
      )}

      <section aria-label="Penanda posisi" data-testid="map-markers" className="overview-section">
        <h3>Penanda posisi (REQ-043)</h3>
        {rows.length === 0 ? (
          <p data-testid="map-markers-empty">Tidak ada posisi live saat ini.</p>
        ) : (
          <table className="overview-table">
            <thead>
              <tr>
                <th scope="col">Driver</th>
                <th scope="col">Delivery Order</th>
                <th scope="col">Koordinat</th>
                <th scope="col">Akurasi</th>
                <th scope="col">Kebaruan</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <MarkerRow key={row.trackingSessionId} row={row} />
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section
        aria-label="Sesi tanpa posisi"
        data-testid="map-unfixed"
        className="overview-section"
      >
        <h3>Sesi aktif tanpa posisi</h3>
        {(derivation?.unfixedSessionCount ?? 0) === 0 &&
        (derivation?.unfixedDeliveryOrders.length ?? 0) === 0 ? (
          <p data-testid="map-unfixed-empty">
            Semua sesi aktif memiliki posisi terakhir.
          </p>
        ) : (
          <div>
            <p data-testid="map-unfixed-count">
              {derivation?.unfixedSessionCount ?? 0} sesi aktif belum memiliki
              posisi — dicatat, tidak dipetakan (tidak ada titik yang
              dikarang).
            </p>
            {(derivation?.unfixedDeliveryOrders.length ?? 0) > 0 && (
              <ul data-testid="map-unfixed-list">
                {derivation?.unfixedDeliveryOrders.map((row) => (
                  <li key={row.deliveryOrderId} data-testid={`unfixed-${row.deliveryOrderId}`}>
                    {row.deliveryOrderId}
                    {row.lastActivityAtEpochMs === null
                      ? ' — belum ada aktivitas tracking'
                      : ` — aktivitas terakhir ${formatFixTime(row.lastActivityAtEpochMs)}`}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function MarkerRow({ row }: { readonly row: MapMarkerRow }): JSX.Element {
  return (
    <tr
      data-testid={`marker-${row.trackingSessionId}`}
      className={row.stale ? 'position-row stale' : 'position-row'}
    >
      <td>{row.driverId}</td>
      <td>{row.deliveryOrderId}</td>
      <td>
        {row.latitude.toFixed(5)}, {row.longitude.toFixed(5)}
      </td>
      <td>{formatAccuracy(row.accuracyMeters)}</td>
      <td>
        {formatAgeMs(row.freshnessMs)}{' '}
        <span
          data-testid={`marker-freshness-${row.trackingSessionId}`}
          className={row.stale ? 'badge badge-stale' : 'badge badge-fresh'}
        >
          {row.stale ? 'Usang' : 'Segar'}
        </span>{' '}
        <span
          data-testid={`marker-fixtime-${row.trackingSessionId}`}
          className="marker-fixtime"
        >
          fix {formatFixTime(row.capturedAtEpochMs)}
        </span>
      </td>
    </tr>
  );
}

/**
 * LiveMapPage — route entry (client permission mirror `tracking.view`;
 * UX only — the server-side guard on the overview endpoints remains the
 * authority).
 */
export function LiveMapPage({
  seams,
}: {
  readonly seams?: LiveMapDashboardSeams;
} = {}): JSX.Element {
  const { session } = useAuth();
  const allowed =
    session !== null && hasPermission(session.staff.roles, 'tracking.view');
  if (!allowed) {
    return (
      <StateView
        state={{
          kind: 'blocked',
          message: 'Peran tidak memiliki izin tracking.view untuk halaman ini.',
        }}
      />
    );
  }
  return <LiveMapDashboard seams={seams ?? {}} />;
}
